// Central shared state for STARCODE. Kept intentionally simple (plain object + small helpers)
// rather than pulling in a state-management library - this is a single-page live-visual tool,
// not an app with complex UI state.

export const state = {
  gravityOn: false,
  fireballOn: false,
  sunOn: false,
  ringOn: false,
  rippleOn: false,
  slowMotion: false,
  morphOn: false,
  morphToggleTime: 0,
  objMorphOn: false,
  objMorphToggleTime: 0,
  loadedObjName: null,
  // Branding morph: spells out STUDIOPESC.COM, mutually exclusive with the other morph targets.
  brandMorphOn: false,
  brandMorphToggleTime: 0,

  // Held (not toggled) negative/positive attractor, exactly like A/S in NEU.pde: force is only
  // applied while the key is physically held down, set directly true/false by keydown/keyup.
  negAttractorHeld: false,
  posAttractorHeld: false,

  // --- Extended effect set, ported from NEU.pde's broader keyboard vocabulary ---
  nebulaOn: false,
  wellsOn: false,
  emberOn: false,
  galaxyDiscOn: false,
  swarmOn: false,
  vortexOn: false,
  galaxySpiralOn: false,
  blackHoleOn: false,
  neutronStarOn: false,
  liquidRippleOn: false,
  inkBleedOn: false,
  portalOn: false,
  causticsOn: false,
  chaosBoostOn: false,
  distortionOn: false,
  fractalOn: false,
  patternOn: false,
  redCircleOn: false,
  shapeChangeOn: false,
  streamsOn: false,
  starSkyOn: false,
  colorInvertOn: false,
  colorMidOn: false,
  scalePulseOn: false,
  // --- Restored NEU.pde number-row audio-reactive toggles (previously missing/replaced) ---
  spiralHighOn: false, // Taste 4 - spiral movement driven by high frequencies
  bandExplodeOn: false, // Taste 5 - explosion triggered per audio band (low/mid/high)
  shimmerOn: false, // Taste 7 - shimmer wave + sparkle driven by mid/high frequencies
  complexOn: false, // Taste ß - complex/ripple2 pulsing single-point travel effect
  permanentColor: 0, // 0 off, 1 blue, 2 red

  // --- NEW audio-reactive colour/light effects -------------------------------------------
  // Bioluminescence: light TRAVELS through the cloud as overlapping waves instead of every
  // particle blinking in unison - an organic, deep-sea-creature feel.
  bioluminescenceOn: false,
  // Each particle is assigned a frequency band and coloured by it (sub=red ... treble=violet),
  // so the swarm literally renders the spectrum.
  spectrumColorOn: false,
  // A hue rainbow that sweeps spatially through the cloud, speed driven by audio.
  chromaWaveOn: false,
  // Colour + brightness flash that fires on detected beats and radiates outward from the centre.
  beatFlashOn: false,
  // Per-particle size driven by audio with a travelling delay (not all growing at once).
  audioSizeOn: false,

  // --- NEW audio-reactive motion effects ---------------------------------------------------
  beatPulseOn: false, // radial shockwave kick on every detected beat
  breathOn: false, // whole cloud inhales/exhales with the bass
  tunnelOn: false, // particles form a travelling tunnel/wormhole down the Z axis
  helixOn: false, // double-helix / DNA strand formation, twist driven by mids
  strobeFieldOn: false, // sharp per-group displacement snaps on transients (spectral flux)

  // Toroidal edge wrap, exactly like NEU.pde's update(): a particle leaving one side of the
  // world box immediately re-enters on the opposite side instead of being pushed back in.
  wrapModeOn: false,

  // Rigid, breathing square concentric grid - snaps the swarm onto nested square rings that
  // pulse inward/outward like a heartbeat. Toggled via its control-bar button only.
  gridPulseOn: false,

  // --- Audio-Reactivity Modulation switches ------------------------------------------------
  // Controls whether non-intrinsically audio-reactive effect groups (Attraktoren, Basis, Effekte,
  // Erweitert) scale dynamically with the music or stay in their static/classic form.
  // Intrinsically audio-reactive effects (Audio-reaktiv, Licht & Farbe, Bewegung) always react.
  audioReactGlobal: true, // Master toggle: "Reactive ON/OFF"
  audioReactAttractors: true,
  audioReactBasis: true,
  audioReactEffects: true,
  audioReactExtended: true,

  // --- Output Window Format / Resolution for Resolume --------------------------------------
  outputPreset: "16:9_1080", // default: 16:9 widescreen Full HD
  outputWidth: 1920,
  outputHeight: 1080,

  // --- Auto-Mix ("Auto-VJ") ----------------------------------------------------------------
  // Automatically rotates through curated audio-reactive effect combinations, switching on
  // musical boundaries (detected beats) rather than a dumb fixed timer.
  autoMixOn: false,
  autoMixIntensity: 0.5, // 0..1 - how often it switches

  // --- Live / Resolume output mode ----------------------------------------------------------
  // Hides every piece of UI (control bar, HUD, cursor) so the browser window is a clean feed
  // that Resolume Arena can pick up via screen/window capture for projector work.
  liveModeOn: false,

  // --- Global performance parameters, all MIDI-controllable (see midi.js) -------------------
  masterIntensity: 1.0, // 0..2 - global force multiplier for every effect
  sizeBoost: 1.0, // 0..3 - global particle size multiplier
  hueShift: 0.0, // 0..1 - global hue rotation
  bloomAmount: 0.45, // 0..3 - post-processing bloom strength
  speedScale: 1.0, // 0..2 - global simulation speed
  rotationSpeed: 0.35, // 0..3 - auto-rotation speed of the camera
  rotationAudioReact: false, // audio-reactivity toggle for camera rotation speed
  spin360On: false, // continuous 360-degree unconstrained rotation loop
  audioGain: 1.0, // 0..3 - multiplies the analysed audio bands before they drive anything
  bioIntensity: 0.85, // 0..2 - Bioluminescence brightness/calmness slider
  bioSpeed: 0.55, // 0..2 - Bioluminescence travel speed multiplier
  colorWheelActive: false, // interactive color spectrum picker toggle
  focusDistance: 780, // 200..1600 - depth of field camera focal plane distance
  dofBlur: 0.45, // 0..2 - depth of field blur / bokeh strength (0 = crisp everywhere)
  dofInvert: false, // false = Weit unscharf (Hintergrund), true = Nah unscharf (Vordergrund)
  dofAudioReact: false, // audio-reactivity toggle for bokeh blur pulsation
  autofocusOn: false, // true = auto-track camera-to-subject focal distance dynamically

  cyberGridOn: false, // 3D infinite Z-axis perspective grid formation
  sphereOn: false, // 3D spherical shell formation
  cameraFollowOn: false, // camera automatically centers and follows swarm center of mass

  sizeCyclePhase: 0, // 0 idle, 1 shrinking, 2 holding small, 3 growing (mirrors the Processing "E" key)
  sizeMultiplier: 1.0,

  explodeTime: -999,
  shockwaveTime: -999,
  shockwaveStrength: 0,

  // Tracks the most-recently-toggled effect so the MIDI mod wheel (CC7) and the on-screen
  // reactive HUD always know which parameter is "currently selected", mirroring the
  // autoMidiTargetId()/applyMidiControlToSelectedTarget() pattern from NEU.pde.
  lastToggledEffect: null,

  midiValue: 0.5, // 0..1, driven by CC7 (mod wheel / fader) when a MIDI device is connected
  midiConnected: false,

  audioReady: false,
};

const SIMPLE_TOGGLES = {
  gravity: "gravityOn",
  fireball: "sunOn",
  fireballOn: "sunOn",
  sun: "sunOn",
  sunOn: "sunOn",
  sonne: "sunOn",
  ring: "ringOn",
  ripple: "rippleOn",
  slowMotion: "slowMotion",
  nebulaOn: "nebulaOn",
  wellsOn: "wellsOn",
  emberOn: "emberOn",
  galaxyDiscOn: "galaxyDiscOn",
  swarmOn: "swarmOn",
  vortexOn: "vortexOn",
  galaxySpiralOn: "galaxySpiralOn",
  blackHole: "blackHoleOn",
  blackHoleOn: "blackHoleOn",
  neutronStar: "neutronStarOn",
  neutronStarOn: "neutronStarOn",
  liquidRippleOn: "liquidRippleOn",
  inkBleedOn: "inkBleedOn",
  portalOn: "portalOn",
  causticsOn: "causticsOn",
  chaosBoostOn: "chaosBoostOn",
  distortionOn: "distortionOn",
  fractalOn: "fractalOn",
  patternOn: "patternOn",
  redCircleOn: "redCircleOn",
  shapeChangeOn: "shapeChangeOn",
  streamsOn: "streamsOn",
  starSkyOn: "starSkyOn",
  colorInvertOn: "colorInvertOn",
  colorMidOn: "colorMidOn",
  scalePulseOn: "scalePulseOn",
  spiralHighOn: "spiralHighOn",
  bandExplodeOn: "bandExplodeOn",
  shimmerOn: "shimmerOn",
  complexOn: "complexOn",
  bioluminescenceOn: "bioluminescenceOn",
  spectrumColorOn: "spectrumColorOn",
  chromaWaveOn: "chromaWaveOn",
  beatFlashOn: "beatFlashOn",
  audioSizeOn: "audioSizeOn",
  beatPulseOn: "beatPulseOn",
  breathOn: "breathOn",
  tunnelOn: "tunnelOn",
  helixOn: "helixOn",
  strobeFieldOn: "strobeFieldOn",
  wrapModeOn: "wrapModeOn",
  autoMixOn: "autoMixOn",
  gridPulseOn: "gridPulseOn",
  cyberGridOn: "cyberGridOn",
  cyberGrid: "cyberGridOn",
  sphereOn: "sphereOn",
  sphere: "sphereOn",
  kugel: "sphereOn",
  spin360On: "spin360On",
  objMorphOn: "objMorphOn",
  dofInvert: "dofInvert",
  dofInvertOn: "dofInvert",
  dofAudioReact: "dofAudioReact",
  dofAudioReactOn: "dofAudioReact",
  rotationAudioReact: "rotationAudioReact",
  rotationAudioReactOn: "rotationAudioReact",
  autofocusOn: "autofocusOn",
  autofocus: "autofocusOn",
  cameraFollowOn: "cameraFollowOn",
  cameraFollow: "cameraFollowOn",
  audioReactGlobal: "audioReactGlobal",
  audioReactAttractors: "audioReactAttractors",
  audioReactBasis: "audioReactBasis",
  audioReactEffects: "audioReactEffects",
  audioReactExtended: "audioReactExtended",
};

export function toggleEffect(name) {
  if (name === "morph") {
    state.morphOn = !state.morphOn;
    if (state.morphOn) { state.objMorphOn = false; state.brandMorphOn = false; }
    state.morphToggleTime = performance.now() / 1000;
    state.lastToggledEffect = name;
    return;
  }
  if (name === "objMorph" || name === "objMorphOn") {
    state.objMorphOn = !state.objMorphOn;
    if (state.objMorphOn) { state.morphOn = false; state.brandMorphOn = false; }
    state.objMorphToggleTime = performance.now() / 1000;
    state.lastToggledEffect = name;
    return;
  }
  if (name === "brandMorph" || name === "brandMorphOn") {
    state.brandMorphOn = !state.brandMorphOn;
    if (state.brandMorphOn) { state.morphOn = false; state.objMorphOn = false; }
    state.brandMorphToggleTime = performance.now() / 1000;
    state.lastToggledEffect = name;
    return;
  }
  const field = SIMPLE_TOGGLES[name];
  if (!field) return;
  state[field] = !state[field];
  state.lastToggledEffect = name;
}

export function setPermanentColor(mode) {
  // 0 off, 1 blue, 2 red - mutually exclusive, matches Cmd+Shift+D / Cmd+Shift+R in NEU.pde.
  state.permanentColor = state.permanentColor === mode ? 0 : mode;
}

export function resetEffects() {
  state.gravityOn = false;
  state.fireballOn = false;
  state.sunOn = false;
  state.ringOn = false;
  state.rippleOn = false;
  state.slowMotion = false;
  state.nebulaOn = false;
  state.wellsOn = false;
  state.emberOn = false;
  state.galaxyDiscOn = false;
  state.swarmOn = false;
  state.vortexOn = false;
  state.galaxySpiralOn = false;
  state.blackHoleOn = false;
  state.neutronStarOn = false;
  state.liquidRippleOn = false;
  state.inkBleedOn = false;
  state.portalOn = false;
  state.causticsOn = false;
  state.chaosBoostOn = false;
  state.distortionOn = false;
  state.fractalOn = false;
  state.patternOn = false;
  state.redCircleOn = false;
  state.shapeChangeOn = false;
  state.streamsOn = false;
  state.starSkyOn = false;
  state.colorInvertOn = false;
  state.colorMidOn = false;
  state.scalePulseOn = false;
  state.spiralHighOn = false;
  state.bandExplodeOn = false;
  state.shimmerOn = false;
  state.complexOn = false;
  state.bioluminescenceOn = false;
  state.spectrumColorOn = false;
  state.chromaWaveOn = false;
  state.beatFlashOn = false;
  state.audioSizeOn = false;
  state.beatPulseOn = false;
  state.breathOn = false;
  state.tunnelOn = false;
  state.helixOn = false;
  state.strobeFieldOn = false;
  state.gridPulseOn = false;
  state.cyberGridOn = false;
  state.sphereOn = false;
  state.spin360On = false;
  state.cameraFollowOn = false;
  state.objMorphOn = false;
  state.brandMorphOn = false;
  state.negAttractorHeld = false;
  state.posAttractorHeld = false;
  state.permanentColor = 0;
  state.sizeCyclePhase = 0;
  state.sizeMultiplier = 1.0;
  state.hueShift = 0.0;
  state.masterIntensity = 1.0;
  state.sizeBoost = 1.0;
  state.bloomAmount = 0.45;
  state.speedScale = 1.0;
  state.rotationSpeed = 0.35;
  state.rotationAudioReact = false;
  state.audioGain = 1.0;
  state.bioIntensity = 0.85;
  state.focusDistance = 780;
  state.dofBlur = 0.45;
  state.dofInvert = false;
  state.dofAudioReact = false;
  state.autofocusOn = false;
  state.colorWheelActive = false;
  state.autoMixOn = false;
  // Morph intentionally left untouched - a full reset shouldn't yank the word away mid-performance.
}
